import {
    calculatorFishingSourceTraces,
} from "./calculator-layer-inspector-contract.js";
import {
    calculatorDataText as t,
} from "./calculator-data-i18n.js";
import {
    calculatorLootProvenanceScalarConditionsText,
} from "./calculator-loot-provenance-presentation.js";

function nonEmptyString(value, path) {
    if (typeof value !== "string" || !value.trim() || value !== value.trim()) {
        throw new TypeError(`${path} must be a non-empty trimmed string`);
    }
    return value;
}

function sourceExpressionString(value, path) {
    if (typeof value !== "string" || !value.trim()) {
        throw new TypeError(`${path} must be a non-blank source expression`);
    }
    return value;
}

export function calculatorRateAlternativeLabel(alternative, path = "alternative") {
    const applicability = alternative?.applicability;
    if (applicability?.kind === "fallback") {
        return t("common.status.default", "Default");
    }
    if (applicability?.kind === "typedConditions") {
        if (!Array.isArray(applicability.conditions) || !applicability.conditions.length) {
            throw new TypeError(`${path}.applicability.conditions must not be empty`);
        }
        return calculatorLootProvenanceScalarConditionsText(
            applicability.conditions,
            `${path}.applicability.conditions`,
        );
    }
    if (applicability?.kind === "sourceExpression") {
        return sourceExpressionString(
            applicability.expression,
            `${path}.applicability.expression`,
        );
    }
    throw new TypeError(`${path}.applicability.kind is unsupported`);
}

function resolutionByFamily(projection) {
    return new Map((projection.rateAlternativeResolutions ?? []).map((resolution) => [
        resolution.familyId,
        resolution,
    ]));
}

/**
 * Projects source-authored alternatives for one loot group. The engine-owned
 * resolution is joined by family identity; candidate status is deliberately
 * not used to infer which condition is active.
 */
export function calculatorRateAlternativeFamiliesForGroup({
    projection,
    fishingSource,
    groupId,
}) {
    const alternatives = new Map();
    for (const trace of calculatorFishingSourceTraces(projection, fishingSource)) {
        if (
            trace.factKey?.groupId !== groupId
            || (trace.factKey.kind !== "groupRate" && trace.factKey.kind !== "itemRate")
        ) {
            continue;
        }
        for (const [candidateIndex, candidate] of (trace.candidates ?? []).entries()) {
            const alternative = candidate.operation?.alternative;
            if (!alternative) {
                continue;
            }
            const path = `${trace.traceId}.candidates[${candidateIndex}].operation.alternative`;
            const familyId = nonEmptyString(alternative.familyId, `${path}.familyId`);
            const alternativeId = nonEmptyString(
                alternative.alternativeId,
                `${path}.alternativeId`,
            );
            const key = `${familyId}\u0000${alternativeId}`;
            const presented = Object.freeze({
                id: alternativeId,
                label: calculatorRateAlternativeLabel(alternative, path),
                title: calculatorRateAlternativeLabel(alternative, path),
                priority: alternative.priority,
            });
            const previous = alternatives.get(key);
            if (previous && JSON.stringify(previous) !== JSON.stringify(presented)) {
                throw new TypeError(`${path} conflicts with another hydrated alternative`);
            }
            alternatives.set(key, presented);
        }
    }

    const resolutions = resolutionByFamily(projection);
    const grouped = new Map();
    for (const [key, option] of alternatives) {
        const familyId = key.slice(0, key.indexOf("\u0000"));
        const family = grouped.get(familyId) ?? [];
        family.push(option);
        grouped.set(familyId, family);
    }
    return Object.freeze([...grouped].map(([familyId, options]) => {
        options.sort((left, right) => (
            right.priority - left.priority || left.id.localeCompare(right.id)
        ));
        const resolution = resolutions.get(familyId);
        if (!resolution) {
            throw new TypeError(`rate alternative family ${familyId} has no engine resolution`);
        }
        const selectedAlternativeId = resolution.kind === "selectionRequired"
            || resolution.kind === "inactive"
            ? null
            : resolution.alternativeId;
        const selectedIndex = selectedAlternativeId === null
            ? null
            : options.findIndex((option) => option.id === selectedAlternativeId);
        if (selectedAlternativeId !== null && selectedIndex < 0) {
            throw new TypeError(
                `rate alternative family ${familyId} resolved an unknown alternative`,
            );
        }
        return Object.freeze({
            familyId,
            resolutionKind: resolution.kind,
            selectedIndex,
            options: Object.freeze(options),
        });
    }).sort((left, right) => left.familyId.localeCompare(right.familyId)));
}
